import { useState } from 'react';
import { BadgeCheck, Banknote, Clock, TriangleAlert } from 'lucide-react';
import {
  Button,
  DataTable,
  ErrorState,
  FilterBar,
  LoadingState,
  MetricCard,
  MetricRow,
  Modal,
  Pager,
  ReasonField,
  SelectableChip,
  Stat,
  StatusPill,
  isReasonValid,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDate, formatDateTime } from '../../lib/format/dates';
import { formatCents, formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { notify } from '../../services/notify';
import { pickParam, stringParam } from '../profiles/people';
import type { StripePayout } from './leversApi';
import { useMarkBankVerified, useStripePayout, useStripePayouts } from './leversQueries';
import { STATUS_TONE, humanStatus } from './parts';

const VIEWS = ['all', 'to_verify', 'needs_review', 'verified'] as const;
type View = (typeof VIEWS)[number];
const VIEW_LABEL: Record<View, string> = {
  all: 'All',
  to_verify: 'Paid, not verified',
  needs_review: 'Needs review',
  verified: 'Bank verified',
};

function viewParams(v: View) {
  switch (v) {
    case 'to_verify':
      return { status: 'paid', bankVerified: false };
    case 'needs_review':
      return { reconciliationStatus: 'needs_review' };
    case 'verified':
      return { bankVerified: true };
    default:
      return {};
  }
}

function columns(): DataTableColumn<StripePayout>[] {
  return [
    {
      id: 'payout',
      header: 'Payout',
      width: '1.4fr',
      cell: (p) => (
        <span className="min-w-0">
          <span className="block truncate font-mono text-[12.5px]">{p.stripePayoutId}</span>
          <span className="block text-xs text-muted">arrives {formatDate(p.arrivalDate, 'UTC')}</span>
        </span>
      ),
    },
    {
      id: 'amount',
      header: 'Amount',
      width: '120px',
      align: 'right',
      numeric: true,
      cell: (p) => formatCents(p.amountCents),
    },
    {
      id: 'status',
      header: 'Stripe',
      width: '130px',
      cell: (p) => (
        <StatusPill tone={STATUS_TONE[p.status] ?? 'neutral'} dot>
          {humanStatus(p.status)}
        </StatusPill>
      ),
    },
    {
      id: 'recon',
      header: 'Reconciliation',
      width: '150px',
      cell: (p) => (
        <StatusPill tone={STATUS_TONE[p.reconciliationStatus] ?? 'neutral'}>
          {humanStatus(p.reconciliationStatus)}
        </StatusPill>
      ),
    },
    {
      id: 'charges',
      header: 'Charges matched',
      width: '130px',
      align: 'right',
      numeric: true,
      cell: (p) => `${formatCount(p.chargesMatchedCount)} / ${formatCount(p.chargesCount)}`,
    },
    {
      id: 'bank',
      header: 'Bank',
      width: '150px',
      cell: (p) =>
        p.bankVerifiedAt ? (
          <StatusPill tone="success" dot>
            Verified {formatDate(p.bankVerifiedAt)}
          </StatusPill>
        ) : (
          <span className="text-muted">Not verified</span>
        ),
    },
  ];
}

/**
 * Money in, capture to bank (B6). Stripe pays out daily; a payout is only
 * money in the bank once a person has checked the bank and verified it —
 * stage 5, the stage that makes its transactions eligible for chef payouts.
 */
export function MoneyInScreen() {
  const table = useTableUrlState();
  const view = pickParam(table.filters.view, VIEWS) ?? 'all';
  const payoutId = stringParam(table.filters.payout);
  const q = useStripePayouts({ page: table.page, pageSize: table.pageSize, ...viewParams(view) });
  const toVerify = useStripePayouts({ page: 1, pageSize: 1, status: 'paid', bankVerified: false });
  const review = useStripePayouts({ page: 1, pageSize: 1, reconciliationStatus: 'needs_review' });
  const inTransit = useStripePayouts({ page: 1, pageSize: 1, status: 'in_transit' });
  const verified = useStripePayouts({ page: 1, pageSize: 1, bankVerified: true });
  const n = (x: { data?: { total: number }; isError: boolean }) =>
    x.data ? formatCount(x.data.total) : x.isError ? '—' : '…';

  return (
    <div className="flex flex-col gap-[18px]">
      <MetricRow>
        <MetricCard
          label="Paid, not verified"
          value={n(toVerify)}
          hint="check the bank, then verify"
          accent="var(--admin-chart-1)"
          icon={<Banknote size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Needs review"
          value={n(review)}
          hint="lines that did not reconcile"
          accent="var(--admin-chart-5)"
          icon={<TriangleAlert size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="In transit"
          value={n(inTransit)}
          hint="on the way to the bank"
          accent="var(--admin-chart-2)"
          icon={<Clock size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Bank verified"
          value={n(verified)}
          hint="money confirmed in the bank"
          accent="var(--admin-chart-3)"
          icon={<BadgeCheck size={15} aria-hidden="true" />}
        />
      </MetricRow>

      <div>
        <FilterBar>
          {VIEWS.map((v) => (
            <SelectableChip
              key={v}
              active={view === v}
              onClick={() => table.setFilter('view', v === 'all' ? undefined : v)}
            >
              {VIEW_LABEL[v]}
            </SelectableChip>
          ))}
        </FilterBar>
        <DataTable
          label="Stripe payouts"
          minWidth={980}
          columns={columns()}
          rows={q.data?.items ?? []}
          getRowId={(p) => p.id}
          onRowClick={(p) => table.setParams({ payout: p.id })}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          emptyLabel="No payouts match that view."
        />
        <Pager
          page={table.page}
          pageSize={table.pageSize}
          total={q.data?.total ?? 0}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          noun="payouts"
        />
      </div>

      {payoutId && <PayoutDialog id={payoutId} onClose={() => table.setParams({ payout: undefined })} />}
    </div>
  );
}

function PayoutDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useStripePayout(id);
  const { locked } = useSession();
  const verify = useMarkBankVerified();
  const [reason, setReason] = useState('');
  const p = q.data;
  const verifiable = !!p && p.status === 'paid' && !p.bankVerifiedAt;
  const verifyLock = locked('moneyIn.write');

  const categories = p
    ? ([
        ['Charges', p.chargesStatus, p.chargesCount, p.chargesAmountCents, p.chargesExceptions],
        ['Refunds', p.refundsStatus, p.refundsCount, p.refundsAmountCents, p.refundsExceptions],
        ['Adjustments', p.adjustmentsStatus, p.adjustmentsCount, p.adjustmentsAmountCents, p.adjustmentsExceptions],
        ['Other (Stripe fees)', '', p.otherCount, p.otherAmountCents, p.otherExceptions],
      ] as const)
    : [];

  return (
    <Modal isOpen onClose={onClose} size="lg" title={p ? `Payout ${p.stripePayoutId}` : 'Payout'}>
      {q.isPending ? (
        <LoadingState label="Loading payout…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        p && (
          <>
            <div className="mb-4 grid grid-cols-4 gap-2.5">
              <Stat label="Amount" value={formatCents(p.amountCents)} />
              <Stat
                label="Stripe"
                value={humanStatus(p.status)}
                hint={p.paidAt ? `paid ${formatDate(p.paidAt)}` : undefined}
              />
              <Stat label="Reconciliation" value={humanStatus(p.reconciliationStatus)} />
              <Stat
                label="Bank"
                value={p.bankVerifiedAt ? 'Verified' : 'Not verified'}
                hint={p.bankVerifiedAt ? formatDateTime(p.bankVerifiedAt) : undefined}
              />
            </div>
            <div className="mb-4">
              {categories.map(([label, status, count, cents, exceptions]) => (
                <div
                  key={label}
                  className="flex items-center gap-3 border-t border-[rgba(143,68,32,.08)] py-2 text-[13px]"
                >
                  <span className="w-44 font-semibold text-ink">{label}</span>
                  <span className="tabular-nums text-muted">
                    {formatCount(count)} · {formatCents(cents)}
                  </span>
                  {status && <StatusPill tone={STATUS_TONE[status] ?? 'neutral'}>{humanStatus(status)}</StatusPill>}
                  {(exceptions?.length ?? 0) > 0 && (
                    <span className="ml-auto text-danger">{exceptions!.length} unmatched</span>
                  )}
                </div>
              ))}
            </div>
            {p.failureMessage && <p className="text-sm text-danger">{p.failureMessage}</p>}
            {verifiable && verifyLock && (
              <div className="flex justify-end border-t border-[rgba(143,68,32,.1)] pt-4">
                <Button size="sm" lockedReason={verifyLock}>
                  Verify in bank
                </Button>
              </div>
            )}
            {verifiable && !verifyLock && (
              <div className="border-t border-[rgba(143,68,32,.1)] pt-4">
                <ReasonField
                  value={reason}
                  onChange={setReason}
                  label="Bank verification note"
                  hint="What you checked — e.g. the deposit on the statement. Logged with who and when."
                />
                {verify.error && <ErrorState error={verify.error} compact />}
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    disabled={!isReasonValid(reason)}
                    loading={verify.isPending}
                    onClick={() =>
                      verify.mutate(
                        { id: p.id, reason: reason.trim() },
                        { onSuccess: () => notify.success('Payout verified in the bank.') },
                      )
                    }
                  >
                    Verify in bank
                  </Button>
                </div>
              </div>
            )}
          </>
        )
      )}
    </Modal>
  );
}
